import { compareDesc } from "date-fns";
import { DateTime } from "luxon";
import { z } from "zod";
import {
  compareISOStringDateRangesByStartAsc,
  isNotNullNotUndefined,
  isoStringDateRangeToTimestampRange,
  timestampRangeToISOStringDateRange
} from "../helpers/utilities.js";
import {
  removeDataExtractionFieldNamePrefix
} from "./customField.js";
import { getAvailabilityDateRanges } from "./schedulingConfiguration.js";
const InterviewStatus = {
  Pending: "pending",
  InProgress: "in_progress",
  Completed: "completed",
  Cancelled: "cancelled"
};
const allInterviewStatuses = [
  InterviewStatus.Pending,
  InterviewStatus.InProgress,
  InterviewStatus.Completed,
  InterviewStatus.Cancelled
];
const interviewStatusSchema = z.enum(allInterviewStatuses);
const InterviewDecision = {
  Advanced: "advanced",
  Rejected: "rejected"
};
const allInterviewDecisions = [
  InterviewDecision.Advanced,
  InterviewDecision.Rejected
];
function isInterviewDecision(value) {
  return allInterviewDecisions.includes(value);
}
const interviewAssessmentFeedbackSchema = z.object({
  sentiment: z.enum(["positive", "negative"]).nullish(),
  suggested_score: z.int().min(1).max(5).nullish(),
  comments: z.string().nullish()
});
const genderOptions = [
  "Male",
  "Female",
  "Decline to Self Identify"
];
const ethnicityOptions = [
  "Hispanic or Latino",
  "Not Hispanic or Latino"
];
const raceOptions = [
  "American Indian or Alaska Native",
  "Asian",
  "Black or African American",
  "White",
  "Native Hawaiian or Other Pacific Islander",
  "Two or more races"
];
const InterviewFeedbackRating = {
  Great: "Great",
  Repetitive: "Repetitive",
  MissingFocusAreas: "Missing focus areas",
  InaccurateAssessment: "Inaccurate assessment",
  Other: "Other feedback"
};
function isCompletedCallMetadata(call) {
  return Boolean(call.start_time) && Boolean(call.end_time);
}
const MeasurementScale = {
  /** A scale consisting of only 1, 2, 3, 4, or 5. */
  FivePointOrdinal: "5_point_ordinal",
  /** A scale consisting of any real number between 1 and 5 inclusive. */
  FivePointInterval: "5_point_interval"
};
const EMPTY_INTERVIEW_INSIGHTS = {
  summary: null,
  sentiment_score: null,
  assessment_score: null,
  assessment_score_measurement: null,
  key_positive_quotes: null,
  key_negative_quotes: null,
  strengths: null,
  weaknesses: null,
  unanswered_candidate_questions: null
};
function buildInterviewPathPrefix(companyId, interviewId) {
  return `${companyId}/interviews/${interviewId}`;
}
const mapInterviewDbToInterview = (interviewDb) => {
  const questions = interviewDb.questions ?? [];
  const insights = {
    ...EMPTY_INTERVIEW_INSIGHTS,
    ...typeof interviewDb.insights === "object" ? interviewDb.insights : {}
  };
  const interview = {
    ...interviewDb,
    questions,
    decision: interviewDb.decision,
    insights
  };
  return interview;
};
const mapInterviewDbToInterviewLoaded = (interviewDb, customFields) => {
  const interview = mapInterviewDbToInterview(interviewDb);
  const interviewLoaded = {
    ...interview,
    custom_fields: customFields
  };
  return interviewLoaded;
};
const mapInterviewAndContactToInterviewWithContact = (interview, contact) => {
  return {
    ...interview,
    contact
  };
};
const mapInterviewInsightsToPublicAPIInsights = (insights) => {
  return {
    summary: insights.summary ?? null,
    sentiment_score: insights.sentiment_score ?? null,
    assessment_score: insights.assessment_score ?? null,
    assessment_score_measurement: insights.assessment_score_measurement ?? null,
    key_positive_quotes: insights.key_positive_quotes ?? null,
    key_negative_quotes: insights.key_negative_quotes ?? null,
    strengths: insights.strengths ?? null,
    weaknesses: insights.weaknesses ?? null
  };
};
const mapInterviewAssessmentFieldsToPublicAPIFormat = (assessment, customFields) => {
  return assessment.custom_field_definitions.map((definition) => {
    const matchingFieldValue = customFields.find(
      (field) => field.custom_field_definition_id === definition.id
    );
    return {
      name: definition.display_name,
      value: matchingFieldValue?.data.value,
      commentary: matchingFieldValue?.data.display_value ?? null
    };
  });
};
function mapInterviewDataExtractionFieldsToPublicAPIFormat(customFieldsAndDefinitions) {
  return customFieldsAndDefinitions.map((customFieldAndDefinition) => {
    const { definition, customField } = customFieldAndDefinition;
    return {
      key: removeDataExtractionFieldNamePrefix(definition),
      name: customField.display_name,
      value: customField.data.value,
      type: customField.field_type,
      commentary: customField.data.display_value ?? null
    };
  });
}
const mapInterviewUserCustomFieldsToPublicAPIFormat = (userCustomFields) => {
  return userCustomFields.map((customField) => ({
    key: customField.field_name,
    name: customField.display_name,
    type: customField.field_type,
    value: customField.data.value
  }));
};
function getNextMeetingAvailabilityDatesDisplay(interviewMetadata, timezone) {
  const availability = interviewMetadata?.next_interview_candidate_availability;
  if (!availability) {
    return null;
  }
  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
    weekday: "long",
    day: "2-digit",
    year: "numeric",
    timeZone: timezone
  });
  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone
  });
  const availabilityRanges = [...availability.availability_ranges || []];
  return availabilityRanges.sort((rangeA, rangeB) => {
    if (rangeA.is_preferred !== rangeB.is_preferred) {
      return rangeA.is_preferred ? -1 : 1;
    }
    return compareISOStringDateRangesByStartAsc(rangeA, rangeB);
  }).map((range) => {
    const startDate = new Date(range.start);
    const endDate = new Date(range.end);
    return {
      date: `From ${timeFormatter.format(startDate)} to ${timeFormatter.format(endDate)}, on ${dateFormatter.format(startDate)}`,
      is_preferred: range.is_preferred
    };
  });
}
function getMostRecentCallStartDate(interviewMetadata) {
  const callStartDates = interviewMetadata?.calls?.map((call) => call.start_time).filter(isNotNullNotUndefined).map((startTime) => new Date(startTime)).sort(compareDesc);
  return callStartDates?.[0];
}
function getNextMeetingDateFromAvailability(interviewMetadata, schedulingConfigurationSettings, nonAvailableDateRanges) {
  const availability = interviewMetadata?.next_interview_candidate_availability?.availability_ranges;
  if (!availability) {
    return null;
  }
  const meetingDurationInMinutes = schedulingConfigurationSettings.duration;
  const sortedAvailability = availability.sort((rangeA, rangeB) => {
    if (rangeA.is_preferred !== rangeB.is_preferred) {
      return rangeA.is_preferred ? -1 : 1;
    }
    return compareISOStringDateRangesByStartAsc(rangeA, rangeB);
  });
  const nextMeetingFirstDate = DateTime.fromJSDate(/* @__PURE__ */ new Date(), {
    zone: schedulingConfigurationSettings.timezone
  }).plus({ days: 1 }).startOf("day");
  const companyRanges = getAvailabilityDateRanges(
    nextMeetingFirstDate.toJSDate(),
    31,
    schedulingConfigurationSettings,
    nonAvailableDateRanges
  );
  const matchingRange = findFirstMatchingRangeWithDuration(
    sortedAvailability,
    companyRanges,
    meetingDurationInMinutes * 60 * 1e3
  );
  return matchingRange ? new Date(matchingRange.start) : null;
}
function findFirstMatchingRangeWithDuration(candidateAvailabilityRanges, companyAvailabilityRanges, durationInMillis) {
  const candidateAvailabilityTimestampRanges = candidateAvailabilityRanges.map(
    isoStringDateRangeToTimestampRange
  );
  const companyAvailabilityTimestampRanges = companyAvailabilityRanges.map(
    isoStringDateRangeToTimestampRange
  );
  for (const candidateRange of candidateAvailabilityTimestampRanges) {
    for (const companyRange of companyAvailabilityTimestampRanges) {
      const startTimestamp = Math.max(
        candidateRange.startTimestamp,
        companyRange.startTimestamp
      );
      const endTimestamp = Math.min(
        candidateRange.endTimestamp,
        companyRange.endTimestamp
      );
      if (endTimestamp - startTimestamp >= durationInMillis) {
        return timestampRangeToISOStringDateRange({
          startTimestamp,
          endTimestamp: startTimestamp + durationInMillis
        });
      }
    }
  }
  return null;
}
export {
  EMPTY_INTERVIEW_INSIGHTS,
  InterviewDecision,
  InterviewStatus,
  MeasurementScale,
  buildInterviewPathPrefix,
  ethnicityOptions,
  findFirstMatchingRangeWithDuration,
  genderOptions,
  getMostRecentCallStartDate,
  getNextMeetingAvailabilityDatesDisplay,
  getNextMeetingDateFromAvailability,
  interviewAssessmentFeedbackSchema,
  interviewStatusSchema,
  isCompletedCallMetadata,
  isInterviewDecision,
  mapInterviewAndContactToInterviewWithContact,
  mapInterviewAssessmentFieldsToPublicAPIFormat,
  mapInterviewDataExtractionFieldsToPublicAPIFormat,
  mapInterviewDbToInterview,
  mapInterviewDbToInterviewLoaded,
  mapInterviewInsightsToPublicAPIInsights,
  mapInterviewUserCustomFieldsToPublicAPIFormat,
  raceOptions
};
//# sourceMappingURL=interview.js.map
